import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { last, remove } from 'lodash';
import { BehaviorSubject } from 'rxjs';
import { distinctUntilChanged, filter, tap } from 'rxjs/operators';

import {
  Board,
  BoardConfiguration,
  BoardOutputBehaviourList,
  BoardOutputTypeList,
  BoardSourceTypeList,
  BoardTypeList,
  ProgressTypeList,
  RestBoardModel,
  disallowSourceTypeOutputTypeMap,
} from '@main-application/boards/interfaces/board';
import { selectPortfolios } from '@portfolio/selectors/portfolio.selectors';
import { AppRoutes } from '@shared/constants/app-routes.const';
import { ButtonType } from '@shared/enums/button-type';
import { IRadioButtonOption } from '@shared/interfaces/radio-button-option.interface';
import { RestPortfolioModel } from '@shared/interfaces/rest-portfolio-model.interface';
import { EnumerationValuePipe } from '@shared/pipes/enumeration-value.pipe';
import { SnackbarService } from '@ui-components/components/customized-snackbar/snackbar.service';

import {
  BoardOutputBehaviour,
  BoardOutputType,
  BoardProgressType,
  BoardSourceType,
  BoardType,
} from '../interfaces/board.enums';
import { BoardService } from '../services/board.service';

@UntilDestroy()
@Component({
  selector: 'app-boards-create',
  templateUrl: './boards-create.component.html',
  styleUrls: ['./boards-create.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BoardsCreateComponent implements OnInit {
  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private dialogRef: MatDialogRef<BoardsCreateComponent, BoardConfiguration>,
    private formBuilder: FormBuilder,
    private boardService: BoardService,
    private store: Store<{}>,
    private cdr: ChangeDetectorRef,
    private snackbarService: SnackbarService,
    private enumerationValuePipe: EnumerationValuePipe,
    @Inject(MAT_DIALOG_DATA) public data?: BoardConfiguration
  ) {}

  private boardActionSubject = new BehaviorSubject(false);
  inProgress$ = this.boardActionSubject.asObservable();
  ButtonType = ButtonType;
  form = this.formBuilder.group({
    selectedPortfolioIds: [this.data?.selectedPortfolioIds],
    boardType: [this.data?.boardType, [Validators.required]],
    name: [this.data?.name, [Validators.required]],
    progressType: [this.data?.progressType, [Validators.required]],
    datasource: [this.data?.datasource, [Validators.required]],
    outputAction: [this.data?.outputAction, []],
    outputBehaviour: [this.data?.outputBehaviour, []],
    boardCellColor: [this.data?.boardCellColor ?? '#ffffff'],
  });
  progressTypeList = ProgressTypeList;
  boardSourceTypeList = BoardSourceTypeList;
  boardTypeList = BoardTypeList;
  boardOutputTypeList = BoardOutputTypeList;
  boardOutputBehaviorList = BoardOutputBehaviourList;
  userPortfolios: IRadioButtonOption<number>[] = [];
  editMode: boolean;
  isSettingsOpened = false;

  ngOnInit(): void {
    this.editMode = !!this.data;
    this.form.patchValue(this.data);
    if (!this.data?.selectedPortfolioIds?.length) {
      this.form.controls.selectedPortfolioIds.setValue([-1]);
    }
    this.store
      .select(selectPortfolios)
      .pipe(
        untilDestroyed(this),
        filter(portfolios => !!portfolios?.length),
        tap((portfolios: RestPortfolioModel[]) => {
          this.userPortfolios = [
            {
              value: -1,
              label: 'All portfolios',
              sort: 0,
            },
          ];
          this.userPortfolios.push(
            ...portfolios.map<IRadioButtonOption<number>>(item => {
              return {
                value: item.id,
                label: item.name,
              };
            })
          );
          this.cdr.detectChanges();
        })
      )
      .subscribe();

    this.form.controls.selectedPortfolioIds.valueChanges
      .pipe(
        untilDestroyed(this),
        distinctUntilChanged(),
        tap(value => {
          if (value?.length > 1 && last(value) > 0) {
            remove(value, x => x < 0);
            this.form.controls.selectedPortfolioIds.setValue(value);
          }
          if (value?.length > 1 && last(value) < 0) {
            this.form.controls.selectedPortfolioIds.setValue([-1]);
          }
          if (!value?.length) {
            this.form.controls.selectedPortfolioIds.setValue([-1]);
          }
          this.cdr.detectChanges();
        })
      )
      .subscribe();

    this.form.controls.boardType.valueChanges
      .pipe(
        distinctUntilChanged(),
        untilDestroyed(this),
        tap(value => {
          if (
            !this.form.value.name ||
            this.boardTypeList
              .flat()
              .map(e => e.label)
              .includes(this.form.value.name)
          ) {
            const selected = this.boardTypeList.flat().find(x => x.value === value);
            if (selected) {
              this.form.controls.name.setValue(selected.label);

              switch (value) {
                case BoardType.MakeReady:
                  this.form.controls.progressType.setValue(BoardProgressType.UpdateMakeReadyProgressBar);
                  this.form.controls.datasource.setValue(BoardSourceType.MakeReadyUnits);
                  this.form.controls.outputAction.setValue(BoardOutputType.SendToApproval);
                  this.form.controls.outputBehaviour.setValue(BoardOutputBehaviour.Automatically);
                  this.form.controls.boardCellColor.setValue('#ffffff');
                  break;
                case BoardType.Reno:
                  this.form.controls.progressType.setValue(BoardProgressType.UpdateRENOProgressBar);
                  this.form.controls.datasource.setValue(BoardSourceType.RenoUnits);
                  this.form.controls.outputAction.setValue(BoardOutputType.SendToApproval);
                  this.form.controls.outputBehaviour.setValue(BoardOutputBehaviour.Automatically);
                  this.form.controls.boardCellColor.setValue('#f5fbfb');
                  break;
                case BoardType.Marketing:
                  this.form.controls.progressType.setValue(BoardProgressType.None);
                  this.form.controls.datasource.setValue(BoardSourceType.UnleasedUnits);
                  this.form.controls.outputAction.setValue(BoardOutputType.None);
                  this.form.controls.outputBehaviour.setValue(null);
                  this.form.controls.boardCellColor.setValue('#fafffa');
                  break;
                case BoardType.Eviction:
                  this.form.controls.progressType.setValue(BoardProgressType.UpdateNoticeProgressBar);
                  this.form.controls.datasource.setValue(BoardSourceType.UnitsInNoticeUnderEviction);
                  this.form.controls.outputAction.setValue(BoardOutputType.SendToMoveOut);
                  this.form.controls.outputBehaviour.setValue(BoardOutputBehaviour.Automatically);
                  this.form.controls.boardCellColor.setValue('#FFF7F7');
                  break;
                case BoardType.MoveIn:
                  this.form.controls.progressType.setValue(BoardProgressType.UpdateMoveInProgressBar);
                  this.form.controls.datasource.setValue(BoardSourceType.UnitsInMoveIn);
                  this.form.controls.outputAction.setValue(BoardOutputType.SendToArchive);
                  this.form.controls.outputBehaviour.setValue(BoardOutputBehaviour.Automatically);
                  this.form.controls.boardCellColor.setValue('#ffffff');
                  break;
                case BoardType.MoveOut:
                  this.form.controls.progressType.setValue(BoardProgressType.UpdateMoveOutProgressBar);
                  this.form.controls.datasource.setValue(BoardSourceType.MoveOutUnits);
                  this.form.controls.outputAction.setValue(BoardOutputType.SendToMakeReady);
                  this.form.controls.outputBehaviour.setValue(BoardOutputBehaviour.Automatically);
                  this.form.controls.boardCellColor.setValue('#ffffff');
                  break;
                case BoardType.Approval:
                  this.form.controls.progressType.setValue(BoardProgressType.UpdateApprovalProgressBar);
                  this.form.controls.datasource.setValue(BoardSourceType.NonPmsUnits);
                  this.form.controls.outputAction.setValue(BoardOutputType.SendToArchive);
                  this.form.controls.outputBehaviour.setValue(BoardOutputBehaviour.Automatically);
                  this.form.controls.boardCellColor.setValue('#ffffff');
                  break;
                case BoardType.NonPms:
                  this.form.controls.progressType.setValue(BoardProgressType.None);
                  this.form.controls.datasource.setValue(BoardSourceType.NonPmsUnits);
                  this.form.controls.outputAction.setValue(BoardOutputType.SendToArchive);
                  this.form.controls.outputBehaviour.setValue(BoardOutputBehaviour.Automatically);
                  this.form.controls.boardCellColor.setValue('#fdfdf2');
                  break;
                case BoardType.Custom:
                  this.form.controls.progressType.setValue(BoardProgressType.None);
                  this.form.controls.datasource.setValue(undefined);
                  this.form.controls.outputAction.setValue(undefined);
                  this.form.controls.outputBehaviour.setValue(BoardOutputBehaviour.Automatically);
                  this.form.controls.boardCellColor.setValue('#ffffff');
                  this.isSettingsOpened = true;
              }
            }
          }

          switch (value) {
            case BoardType.MakeReady:
            case BoardType.Reno:
              this.form.controls.outputAction.setValidators(Validators.required);
              this.form.controls.outputAction.updateValueAndValidity();
              break;
            case BoardType.Marketing:
              this.form.controls.outputAction.clearValidators();
              this.form.controls.outputAction.updateValueAndValidity();
              break;
          }

          this.cdr.detectChanges();
        })
      )
      .subscribe();

    if (!this.editMode) {
      this.form.controls.boardType.setValue(BoardType.MakeReady);
    }
  }

  toggleAdvancedSettings() {
    this.isSettingsOpened = !this.isSettingsOpened;
  }

  ok() {
    if (
      this.form.value.outputBehaviour === BoardOutputBehaviour.Automatically &&
      this.form.value.datasource != null &&
      this.form.value.outputAction != null &&
      disallowSourceTypeOutputTypeMap[this.form.value.datasource] === this.form.value.outputAction
    ) {
      this.snackbarService.error(
        `Turns cannot auto-progress to the same step they started in. Either set “Remove rows” to "manual" or choose an ending destination that is not "${this.enumerationValuePipe.transform(
          this.form.value.outputAction,
          BoardOutputTypeList
        )}"`
      );
      return;
    }

    if (this.form.valid) {
      this.boardActionSubject.next(true);
      if (this.editMode) {
        this.dialogRef.close({
          ...this.data,
          ...this.form.value,
        });
        return;
      }
      const board = new Board(Object.assign({}, this.data, this.form.value));
      this.boardService.add(new RestBoardModel(board)).subscribe(id => {
        this.router.navigate([AppRoutes.BOARDS, id], { relativeTo: this.activatedRoute });
        this.dialogRef.close();
      });
    } else {
      this.form.markAllAsTouched();
    }
  }

  close() {
    this.dialogRef.close();
  }
}
